import type { Person } from "../types";
import type {
  BookingRecord,
  FinanceDataReliability,
  FinanceAlertSeverity,
  LabourAlignmentAlert,
  LabourAlignmentArtistProjectWeekRow,
  LabourAlignmentArtistSummary,
  LabourAlignmentDepartmentSummary,
  LabourAlignmentProjectSummary,
  LabourAlignmentReasonCode,
  LabourAlignmentReport,
  LabourAlignmentStatus,
  LabourAlignmentSummary,
  LabourAlignmentThresholds,
  Task,
  TimeLog,
  VersionMirrorRecord,
} from "../models/production";

type ProjectLike = {
  id: string;
  projectName?: string;
  setup?: {
    projectName?: string;
    currency?: string;
    financeCurrency?: string;
    status?: string;
  };
};

interface LabourAlignmentProjectFinanceContext {
  current_timelog_gross_margin?: number;
  current_timelog_margin_percent?: number;
  currency?: string;
  project_status?: string;
}

interface BuildLabourAlignmentReportArgs {
  people: Array<Pick<Person, "id" | "displayName" | "fullName" | "name" | "department" | "dailyRate">>;
  projects?: ProjectLike[];
  tasks?: Task[];
  timeLogs?: TimeLog[];
  bookings?: BookingRecord[];
  versions?: VersionMirrorRecord[];
  startDate: string;
  endDate: string;
  thresholds?: Partial<LabourAlignmentThresholds>;
  projectFinanceContext?: Record<string, LabourAlignmentProjectFinanceContext>;
  now?: string;
}

const DEFAULT_THRESHOLDS: LabourAlignmentThresholds = {
  minutesPerDay: 480,
  alignedLogCoverageMin: 0.8,
  alignedVersionCoverageMin: 0.4,
  underLoggedLogCoverageMax: 0.75,
  underLoggedVersionCoverageMin: 0.4,
  bookedLowVisibilityLogCoverageMax: 0.35,
  bookedLowVisibilityVersionCoverageMax: 0.2,
  versionWithoutLogsLoggedDaysMax: 0.25,
  underAllocatedCoverageMax: 0.6,
  overloadedCoverageMin: 1.1,
  highHiddenLabourCostDays: 2,
};

interface RowAccumulator {
  artistId: string;
  artistName: string;
  projectId: string;
  projectName: string;
  weekStart: string;
  weekEnd: string;
  department?: string;
  artistDailyRate: number;
  bookedMinutes: number;
  loggedMinutes: number;
  versionActivityCount: number;
  versionActiveDateSet: Set<string>;
  allocatedBidMinutes: number;
  bookingIds: Set<string>;
  timeLogIds: Set<string>;
  versionIds: Set<string>;
  linkedTaskIds: Set<string>;
  linkedEntityMap: Map<string, { entityId?: string; entityType?: string; entityName?: string; taskId?: string; pipelineStep?: string; department?: string }>;
  bookingItems: Array<{ id: string; label: string; secondary?: string; metric?: string }>;
  timelogItems: Array<{ id: string; label: string; secondary?: string; metric?: string }>;
  versionItems: Array<{ id: string; label: string; secondary?: string; metric?: string }>;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function roundNumber(value: number, precision = 2) {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

function toIsoDate(value: Date | string) {
  const date =
    typeof value === "string"
      ? new Date(value.length > 10 ? value : `${value}T00:00:00.000Z`)
      : value;
  return date.toISOString().slice(0, 10);
}

function parseIsoDate(value?: string) {
  if (!value) return null;
  const normalized = value.length > 10 ? value.slice(0, 10) : value;
  const date = new Date(`${normalized}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) ? date : null;
}

function addUtcDays(date: Date, days: number) {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function startOfIsoWeek(value: string | Date) {
  const date = typeof value === "string" ? parseIsoDate(value) : value;
  if (!date) return null;
  const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() - day + 1);
  return utcDate;
}

function listIsoWeeksBetween(startDate: string, endDate: string) {
  const start = startOfIsoWeek(startDate);
  const end = startOfIsoWeek(endDate);
  if (!start || !end || end < start) return [] as string[];
  const weeks: string[] = [];
  let cursor = new Date(start.getTime());
  while (cursor <= end) {
    weeks.push(toIsoDate(cursor));
    cursor = addUtcDays(cursor, 7);
  }
  return weeks;
}

function countWorkingDaysBetween(startIso?: string, endIso?: string) {
  if (!startIso || !endIso) return 0;
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (!start || !end || end < start) return 0;
  let total = 0;
  const cursor = new Date(start.getTime());
  while (cursor <= end) {
    const weekday = cursor.getUTCDay() || 7;
    if (weekday >= 1 && weekday <= 5) total += 1;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return total;
}

function getTaskWeekAllocation(task: Task, weekStart: string, weekEnd: string, minutesPerDay: number) {
  const bidDays = typeof task.bidHours === "number" && Number.isFinite(task.bidHours) ? task.bidHours : 0;
  if (bidDays <= 0) {
    return { allocatedMinutes: 0, usedScheduleAssumption: false };
  }

  const scheduleStart = task.plannedStartDate || task.createdAt?.slice(0, 10);
  const scheduleEnd = task.plannedEndDate || task.dueDate || task.plannedStartDate || task.updatedAt?.slice(0, 10) || scheduleStart;

  if (!scheduleStart || !scheduleEnd) {
    return { allocatedMinutes: bidDays * minutesPerDay, usedScheduleAssumption: true };
  }

  const taskWorkingDays = countWorkingDaysBetween(scheduleStart, scheduleEnd);
  const overlapStart = scheduleStart > weekStart ? scheduleStart : weekStart;
  const overlapEnd = scheduleEnd < weekEnd ? scheduleEnd : weekEnd;
  const overlapWorkingDays = countWorkingDaysBetween(overlapStart, overlapEnd);

  if (taskWorkingDays <= 0) {
    return { allocatedMinutes: bidDays * minutesPerDay, usedScheduleAssumption: true };
  }

  return {
    allocatedMinutes: (bidDays * overlapWorkingDays * minutesPerDay) / taskWorkingDays,
    usedScheduleAssumption: false,
  };
}

function getArtistName(person?: Pick<Person, "displayName" | "fullName" | "name" | "id">, fallback?: string) {
  return person?.displayName || person?.fullName || person?.name || fallback || "Unknown artist";
}

function getProjectName(project?: ProjectLike, fallback?: string) {
  return project?.setup?.projectName || project?.projectName || fallback || "Unknown project";
}

function getProjectCurrency(project?: ProjectLike, finance?: LabourAlignmentProjectFinanceContext) {
  return finance?.currency || project?.setup?.financeCurrency || project?.setup?.currency || "GBP";
}

function ratio(numerator: number, denominator: number) {
  if (denominator <= 0) return undefined;
  return numerator / denominator;
}

function confidenceFromSignals(args: {
  bookedMinutes: number;
  loggedMinutes: number;
  versionActivityCount: number;
  allocatedBidMinutes: number;
  reasonCodes: LabourAlignmentReasonCode[];
}): FinanceDataReliability {
  let score = 0;
  if (args.bookedMinutes > 0) score += 35;
  if (args.loggedMinutes > 0) score += 25;
  if (args.versionActivityCount > 0) score += 20;
  if (args.allocatedBidMinutes > 0) score += 15;
  if (args.reasonCodes.includes("low_signal_confidence")) score -= 20;
  if (args.reasonCodes.includes("mixed_signal_review")) score -= 15;
  if (score >= 70) return "high";
  if (score >= 40) return "medium";
  return "low";
}

function reasonSeverity(reasonCodes: LabourAlignmentReasonCode[]): FinanceAlertSeverity {
  if (
    reasonCodes.includes("booked_but_low_logs") ||
    reasonCodes.includes("booked_but_low_visibility") ||
    reasonCodes.includes("versions_without_logs")
  ) {
    return "warning";
  }
  if (reasonCodes.includes("under_allocated_vs_booking") || reasonCodes.includes("allocation_exceeds_booking")) {
    return "critical";
  }
  return "info";
}

function deriveStatus(args: {
  bookedDays: number;
  loggedDays: number;
  versionActiveDays: number;
  bookedMinutes: number;
  loggedMinutes: number;
  allocatedBidMinutes: number;
  versionActivityCount: number;
  thresholds: LabourAlignmentThresholds;
}): { status: LabourAlignmentStatus; reasonCodes: LabourAlignmentReasonCode[]; notes: string[] } {
  const {
    bookedDays,
    loggedDays,
    versionActiveDays,
    bookedMinutes,
    loggedMinutes,
    allocatedBidMinutes,
    versionActivityCount,
    thresholds,
  } = args;

  const reasonCodes: LabourAlignmentReasonCode[] = [];
  const notes: string[] = [];
  const logCoverage = ratio(loggedMinutes, bookedMinutes) ?? 0;
  const versionCoverage = ratio(versionActiveDays, bookedDays) ?? 0;
  const allocationCoverage = ratio(allocatedBidMinutes, bookedMinutes) ?? 0;

  if (bookedMinutes > 0) reasonCodes.push("bookings_present");
  else reasonCodes.push("bookings_missing");
  if (loggedMinutes > 0) reasonCodes.push("timelogs_present");
  else reasonCodes.push("timelogs_missing");
  if (versionActivityCount > 0) reasonCodes.push("versions_present");
  else reasonCodes.push("versions_missing");

  if (bookedMinutes <= 0 && loggedMinutes > 0) {
    reasonCodes.push("logs_without_booking");
    notes.push("Timelog activity exists without booking coverage on this project-week.");
    return { status: "logged_without_booking", reasonCodes, notes };
  }

  if (bookedMinutes <= 0 && versionActivityCount > 0 && loggedDays <= thresholds.versionWithoutLogsLoggedDaysMax) {
    reasonCodes.push("versions_without_logs", "low_signal_confidence");
    notes.push("Visible version activity exists without booking coverage and with little or no logged time.");
    return { status: "version_without_logs", reasonCodes, notes };
  }

  if (bookedMinutes > 0 && allocationCoverage > thresholds.overloadedCoverageMin) {
    reasonCodes.push("allocation_exceeds_booking");
  }
  if (bookedMinutes > 0 && logCoverage > thresholds.overloadedCoverageMin) {
    reasonCodes.push("logged_exceeds_booking");
  }

  if (bookedMinutes > 0 && allocationCoverage > 0 && allocationCoverage < thresholds.underAllocatedCoverageMax) {
    reasonCodes.push("under_allocated_vs_booking");
  }

  if (bookedMinutes > 0 && loggedMinutes <= 0 && versionActivityCount > 0) {
    reasonCodes.push("versions_without_logs", "activity_signal_supports_work");
    notes.push("Version publishes suggest work activity on days with little or no logged time.");
    return { status: "version_without_logs", reasonCodes, notes };
  }

  if (bookedMinutes > 0 && logCoverage <= thresholds.underLoggedLogCoverageMax && versionCoverage >= thresholds.underLoggedVersionCoverageMin) {
    reasonCodes.push("booked_but_low_logs", "activity_signal_supports_work");
    notes.push("Booking coverage and version activity suggest work happened, but timelogs are materially low.");
    return { status: "under_logged_active", reasonCodes, notes };
  }

  if (bookedMinutes > 0 && logCoverage <= thresholds.bookedLowVisibilityLogCoverageMax && versionCoverage <= thresholds.bookedLowVisibilityVersionCoverageMax) {
    reasonCodes.push("booked_but_low_visibility", "non_versioned_work_possible", "low_signal_confidence");
    notes.push("The artist was booked, but both logged time and visible version activity stayed low.");
    return { status: "booked_low_visibility", reasonCodes, notes };
  }

  if (bookedMinutes > 0 && allocationCoverage > 0 && allocationCoverage < thresholds.underAllocatedCoverageMax) {
    notes.push("Bid allocation appears light relative to the booked resource on the project.");
    return { status: "under_allocated", reasonCodes, notes };
  }

  if (
    bookedMinutes > 0 &&
    (logCoverage > thresholds.overloadedCoverageMin || allocationCoverage > thresholds.overloadedCoverageMin)
  ) {
    notes.push("Logged time or allocated bid work materially exceeds the booked resource signal for this week.");
    return { status: "overloaded", reasonCodes, notes };
  }

  if (
    bookedMinutes > 0 &&
    logCoverage >= thresholds.alignedLogCoverageMin &&
    versionCoverage >= thresholds.alignedVersionCoverageMin
  ) {
    reasonCodes.push("activity_signal_supports_work");
    notes.push("Bookings, timelogs, and visible activity broadly support each other for this week.");
    return { status: "aligned", reasonCodes, notes };
  }

  reasonCodes.push("mixed_signal_review", "low_signal_confidence");
  notes.push("Signals are incomplete or conflicting enough that a manager should review this week.");
  return { status: "mixed_review_needed", reasonCodes, notes };
}

function sourceAlignmentSummary(statusCounts: Partial<Record<LabourAlignmentStatus, number>>) {
  const parts = Object.entries(statusCounts)
    .filter(([, count]) => (count || 0) > 0)
    .sort((a, b) => (b[1] || 0) - (a[1] || 0))
    .slice(0, 3)
    .map(([status, count]) => `${count} ${status.replaceAll("_", " ")}`);
  return parts.length > 0 ? parts.join(" • ") : "No alignment signal yet";
}

function primaryStatusFromCounts(statusCounts: Partial<Record<LabourAlignmentStatus, number>>) {
  const ranked = Object.entries(statusCounts)
    .filter(([, count]) => (count || 0) > 0)
    .sort((a, b) => (b[1] || 0) - (a[1] || 0));
  return (ranked[0]?.[0] as LabourAlignmentStatus | undefined) || "mixed_review_needed";
}

function createAccumulator(params: {
  artistId: string;
  artistName: string;
  projectId: string;
  projectName: string;
  weekStart: string;
  weekEnd: string;
  department?: string;
  artistDailyRate: number;
}): RowAccumulator {
  return {
    artistId: params.artistId,
    artistName: params.artistName,
    projectId: params.projectId,
    projectName: params.projectName,
    weekStart: params.weekStart,
    weekEnd: params.weekEnd,
    department: params.department,
    artistDailyRate: params.artistDailyRate,
    bookedMinutes: 0,
    loggedMinutes: 0,
    versionActivityCount: 0,
    versionActiveDateSet: new Set<string>(),
    allocatedBidMinutes: 0,
    bookingIds: new Set<string>(),
    timeLogIds: new Set<string>(),
    versionIds: new Set<string>(),
    linkedTaskIds: new Set<string>(),
    linkedEntityMap: new Map(),
    bookingItems: [],
    timelogItems: [],
    versionItems: [],
  };
}

function pushUniqueItem(collection: Array<{ id: string; label: string; secondary?: string; metric?: string }>, item: {
  id: string;
  label: string;
  secondary?: string;
  metric?: string;
}) {
  if (collection.some((entry) => entry.id === item.id)) return;
  collection.push(item);
}

export function buildLabourAlignmentReport(args: BuildLabourAlignmentReportArgs): LabourAlignmentReport {
  const thresholds = { ...DEFAULT_THRESHOLDS, ...(args.thresholds || {}) };
  const generatedAt = args.now || new Date().toISOString();
  const peopleById = new Map(args.people.map((person) => [person.id, person]));
  const projectsById = new Map((args.projects || []).map((project) => [project.id, project]));
  const weekStarts = listIsoWeeksBetween(args.startDate, args.endDate);
  const weekRanges = new Map(
    weekStarts.map((weekStart) => [weekStart, { weekStart, weekEnd: toIsoDate(addUtcDays(startOfIsoWeek(weekStart)!, 6)) }]),
  );

  const rowMap = new Map<string, RowAccumulator>();

  const getOrCreateRow = (params: {
    artistId: string;
    projectId: string;
    weekStart: string;
    department?: string;
    artistNameFallback?: string;
    projectNameFallback?: string;
  }) => {
    const week = weekRanges.get(params.weekStart);
    if (!week) return null;
    const key = `${params.artistId}:${params.projectId}:${params.weekStart}`;
    if (!rowMap.has(key)) {
      const person = peopleById.get(params.artistId);
      const project = projectsById.get(params.projectId);
      rowMap.set(
        key,
        createAccumulator({
          artistId: params.artistId,
          artistName: getArtistName(person, params.artistNameFallback),
          projectId: params.projectId,
          projectName: getProjectName(project, params.projectNameFallback),
          weekStart: week.weekStart,
          weekEnd: week.weekEnd,
          department: params.department || person?.department || undefined,
          artistDailyRate: typeof person?.dailyRate === "number" && Number.isFinite(person.dailyRate) ? person.dailyRate : 0,
        }),
      );
    }
    const row = rowMap.get(key)!;
    if (!row.department && params.department) row.department = params.department;
    return row;
  };

  for (const booking of args.bookings || []) {
    if (booking.isDeleted || !booking.personId || !booking.projectId || !booking.startDate || !booking.endDate) continue;
    for (const weekStart of weekStarts) {
      const week = weekRanges.get(weekStart)!;
      if (booking.endDate < week.weekStart || booking.startDate > week.weekEnd) continue;
      const overlapStart = booking.startDate > week.weekStart ? booking.startDate : week.weekStart;
      const overlapEnd = booking.endDate < week.weekEnd ? booking.endDate : week.weekEnd;
      const overlapWorkingDays = countWorkingDaysBetween(overlapStart, overlapEnd);
      if (overlapWorkingDays <= 0) continue;
      const percent = typeof booking.percentageAllocation === "number" && Number.isFinite(booking.percentageAllocation)
        ? booking.percentageAllocation / 100
        : 1;
      const bookedMinutes = roundNumber(overlapWorkingDays * thresholds.minutesPerDay * percent, 0);
      const row = getOrCreateRow({
        artistId: booking.personId,
        projectId: booking.projectId,
        weekStart,
        department: booking.department,
        artistNameFallback: booking.personName,
        projectNameFallback: booking.fptProjectName,
      });
      if (!row) continue;
      row.bookedMinutes += bookedMinutes;
      row.bookingIds.add(booking.id);
      pushUniqueItem(row.bookingItems, {
        id: booking.id,
        label: booking.fptProjectName || row.projectName,
        secondary: `${overlapStart} → ${overlapEnd}`,
        metric: `${roundNumber(bookedMinutes / thresholds.minutesPerDay, 1)} days`,
      });
    }
  }

  for (const timeLog of args.timeLogs || []) {
    if (!timeLog.personId || !timeLog.projectId || !timeLog.logDate) continue;
    const weekStartDate = startOfIsoWeek(timeLog.logDate);
    if (!weekStartDate) continue;
    const weekStart = toIsoDate(weekStartDate);
    if (!weekRanges.has(weekStart)) continue;
    const row = getOrCreateRow({
      artistId: timeLog.personId,
      projectId: timeLog.projectId,
      weekStart,
    });
    if (!row) continue;
    row.loggedMinutes += timeLog.minutes || 0;
    row.timeLogIds.add(timeLog.id);
    if (timeLog.taskId) row.linkedTaskIds.add(timeLog.taskId);
    pushUniqueItem(row.timelogItems, {
      id: timeLog.id,
      label: timeLog.description || timeLog.taskId || "Timelog",
      secondary: timeLog.logDate,
      metric: `${roundNumber((timeLog.minutes || 0) / thresholds.minutesPerDay, 1)} days`,
    });
  }

  for (const version of args.versions || []) {
    if (version.isDeleted || !version.personId || !version.projectId) continue;
    const activityDate = version.publishDateNormalized || version.createdAtSource?.slice(0, 10);
    if (!activityDate) continue;
    const weekStartDate = startOfIsoWeek(activityDate);
    if (!weekStartDate) continue;
    const weekStart = toIsoDate(weekStartDate);
    if (!weekRanges.has(weekStart)) continue;
    const row = getOrCreateRow({
      artistId: version.personId,
      projectId: version.projectId,
      weekStart,
      department: version.department,
      artistNameFallback: version.artistName,
      projectNameFallback: version.projectName,
    });
    if (!row) continue;
    row.versionActivityCount += 1;
    row.versionActiveDateSet.add(activityDate);
    row.versionIds.add(version.id);
    if (version.taskId) row.linkedTaskIds.add(version.taskId);
    const entityKey = `${version.linkedEntityType || "unknown"}:${version.linkedEntityId || version.linkedEntityName || version.id}`;
    if (!row.linkedEntityMap.has(entityKey)) {
      row.linkedEntityMap.set(entityKey, {
        entityId: version.linkedEntityId,
        entityType: version.linkedEntityType,
        entityName: version.linkedEntityName,
        taskId: version.taskId,
        pipelineStep: version.pipelineStep,
        department: version.department,
      });
    }
    pushUniqueItem(row.versionItems, {
      id: version.id,
      label: version.versionName || version.versionCode || "Version activity",
      secondary: `${activityDate}${version.linkedEntityName ? ` • ${version.linkedEntityName}` : ""}`,
      metric: version.status || version.pipelineStep || undefined,
    });
  }

  for (const task of args.tasks || []) {
    const assignedArtists = (task.assignedArtistIds || []).filter(Boolean);
    if (!task.projectId || assignedArtists.length === 0) continue;
    for (const weekStart of weekStarts) {
      const week = weekRanges.get(weekStart)!;
      const allocation = getTaskWeekAllocation(task, week.weekStart, week.weekEnd, thresholds.minutesPerDay);
      if (allocation.allocatedMinutes <= 0) continue;
      const share = allocation.allocatedMinutes / Math.max(assignedArtists.length, 1);
      for (const artistId of assignedArtists) {
        const row = getOrCreateRow({
          artistId,
          projectId: task.projectId,
          weekStart,
          department: task.taskType,
        });
        if (!row) continue;
        row.allocatedBidMinutes += share;
        row.linkedTaskIds.add(task.id);
      }
    }
  }

  const rows: LabourAlignmentArtistProjectWeekRow[] = Array.from(rowMap.values())
    .map((row) => {
      const bookedDays = roundNumber(row.bookedMinutes / thresholds.minutesPerDay);
      const loggedDays = roundNumber(row.loggedMinutes / thresholds.minutesPerDay);
      const versionActiveDays = row.versionActiveDateSet.size;
      const allocatedBidDays = roundNumber(row.allocatedBidMinutes / thresholds.minutesPerDay);
      const bookedLabourCost = roundNumber(bookedDays * row.artistDailyRate);
      const loggedLabourCost = roundNumber(loggedDays * row.artistDailyRate);
      const bookingVsLoggedCostGap = roundNumber(bookedLabourCost - loggedLabourCost);
      const possibleHiddenLabourCost = roundNumber(Math.max(bookingVsLoggedCostGap, 0));
      const logCoverageRatio = ratio(row.loggedMinutes, row.bookedMinutes);
      const versionActivityRatio = ratio(versionActiveDays, bookedDays);
      const allocationCoverageRatio = ratio(row.allocatedBidMinutes, row.bookedMinutes);
      const estimatedVisibilityScore = clamp(
        Math.round(
          ((versionActivityRatio || 0) * 65) +
          Math.min(row.versionActivityCount, 10) * 3.5 +
          Math.min(row.linkedEntityMap.size, 6) * 2.5,
        ),
        0,
        100,
      );

      const { status, reasonCodes, notes } = deriveStatus({
        bookedDays,
        loggedDays,
        versionActiveDays,
        bookedMinutes: row.bookedMinutes,
        loggedMinutes: row.loggedMinutes,
        allocatedBidMinutes: row.allocatedBidMinutes,
        versionActivityCount: row.versionActivityCount,
        thresholds,
      });

      const confidenceCategory = confidenceFromSignals({
        bookedMinutes: row.bookedMinutes,
        loggedMinutes: row.loggedMinutes,
        versionActivityCount: row.versionActivityCount,
        allocatedBidMinutes: row.allocatedBidMinutes,
        reasonCodes,
      });

      const dataQualityRisk = clamp(
        roundNumber(
          (possibleHiddenLabourCost / Math.max(row.artistDailyRate || 1, 1)) * 10 +
          ((1 - (logCoverageRatio || 0)) * 45) +
          ((1 - (versionActivityRatio || 0)) * 20),
        ),
        0,
        100,
      );

      return {
        id: `${row.artistId}:${row.projectId}:${row.weekStart}`,
        artist_id: row.artistId,
        artist_name: row.artistName,
        project_id: row.projectId,
        project_name: row.projectName,
        week_start: row.weekStart,
        week_end: row.weekEnd,
        department: row.department,
        booked_minutes: roundNumber(row.bookedMinutes, 0),
        booked_days: bookedDays,
        logged_minutes: roundNumber(row.loggedMinutes, 0),
        logged_days: loggedDays,
        version_activity_count: row.versionActivityCount,
        version_active_days: versionActiveDays,
        allocated_bid_minutes: roundNumber(row.allocatedBidMinutes, 0),
        allocated_bid_days: allocatedBidDays,
        artist_daily_rate: row.artistDailyRate,
        booked_labour_cost: bookedLabourCost,
        logged_labour_cost: loggedLabourCost,
        booking_vs_logged_cost_gap: bookingVsLoggedCostGap,
        possible_hidden_labour_cost: possibleHiddenLabourCost,
        margin_at_risk_from_under_logging: possibleHiddenLabourCost,
        data_quality_risk: dataQualityRisk,
        estimated_visibility_score: estimatedVisibilityScore,
        log_coverage_ratio: logCoverageRatio,
        version_activity_ratio: versionActivityRatio,
        booking_vs_logged_gap_minutes: roundNumber(row.bookedMinutes - row.loggedMinutes, 0),
        booking_vs_version_gap_days: roundNumber(bookedDays - versionActiveDays),
        log_vs_version_gap_days: roundNumber(loggedDays - versionActiveDays),
        allocation_coverage_ratio: allocationCoverageRatio,
        alignment_status: status,
        confidence_category: confidenceCategory,
        reason_codes: Array.from(new Set(reasonCodes)),
        booking_ids: Array.from(row.bookingIds),
        time_log_ids: Array.from(row.timeLogIds),
        version_ids: Array.from(row.versionIds),
        linked_task_ids: Array.from(row.linkedTaskIds),
        linked_entities: Array.from(row.linkedEntityMap.values()),
        booking_items: row.bookingItems,
        timelog_items: row.timelogItems,
        version_items: row.versionItems,
        notes,
      };
    })
    .sort((a, b) =>
      a.week_start.localeCompare(b.week_start) ||
      a.project_name.localeCompare(b.project_name) ||
      a.artist_name.localeCompare(b.artist_name),
    );

  const projectSummaries: Record<string, LabourAlignmentProjectSummary> = {};
  const artistSummaries: Record<string, LabourAlignmentArtistSummary> = {};
  const departmentSummaries: Record<string, LabourAlignmentDepartmentSummary> = {};
  const alerts: LabourAlignmentAlert[] = [];

  for (const row of rows) {
    const projectFinance = args.projectFinanceContext?.[row.project_id];
    const projectCurrency = getProjectCurrency(projectsById.get(row.project_id), projectFinance);
    const projectStatus = projectFinance?.project_status || projectsById.get(row.project_id)?.setup?.status;

    if (!projectSummaries[row.project_id]) {
      projectSummaries[row.project_id] = {
        project_id: row.project_id,
        project_name: row.project_name,
        project_status: projectStatus,
        currency: projectCurrency,
        booked_minutes: 0,
        booked_days: 0,
        logged_minutes: 0,
        logged_days: 0,
        version_activity_count: 0,
        version_active_days: 0,
        allocated_bid_minutes: 0,
        allocated_bid_days: 0,
        total_booked_labour_cost: 0,
        total_logged_labour_cost: 0,
        possible_hidden_labour_cost: 0,
        logged_coverage_percent: 0,
        version_activity_coverage_percent: 0,
        number_of_under_logged_active_weeks: 0,
        number_of_booked_low_visibility_weeks: 0,
        number_of_logged_without_booking_weeks: 0,
        number_of_version_without_logs_weeks: 0,
        confidence_category: "high",
        source_alignment_summary: "",
        current_timelog_gross_margin: projectFinance?.current_timelog_gross_margin,
        booking_cost_margin_estimate: projectFinance?.current_timelog_gross_margin,
        margin_difference_due_to_alignment_gap: 0,
        primary_status: "aligned",
        status_counts: {},
      };
    }
    const projectSummary = projectSummaries[row.project_id];
    projectSummary.booked_minutes += row.booked_minutes;
    projectSummary.booked_days += row.booked_days;
    projectSummary.logged_minutes += row.logged_minutes;
    projectSummary.logged_days += row.logged_days;
    projectSummary.version_activity_count += row.version_activity_count;
    projectSummary.version_active_days += row.version_active_days;
    projectSummary.allocated_bid_minutes += row.allocated_bid_minutes;
    projectSummary.allocated_bid_days += row.allocated_bid_days;
    projectSummary.total_booked_labour_cost += row.booked_labour_cost;
    projectSummary.total_logged_labour_cost += row.logged_labour_cost;
    projectSummary.possible_hidden_labour_cost += row.possible_hidden_labour_cost;
    projectSummary.status_counts[row.alignment_status] = (projectSummary.status_counts[row.alignment_status] || 0) + 1;
    if (row.alignment_status === "under_logged_active") projectSummary.number_of_under_logged_active_weeks += 1;
    if (row.alignment_status === "booked_low_visibility") projectSummary.number_of_booked_low_visibility_weeks += 1;
    if (row.alignment_status === "logged_without_booking") projectSummary.number_of_logged_without_booking_weeks += 1;
    if (row.alignment_status === "version_without_logs") projectSummary.number_of_version_without_logs_weeks += 1;

    if (!artistSummaries[row.artist_id]) {
      artistSummaries[row.artist_id] = {
        artist_id: row.artist_id,
        artist_name: row.artist_name,
        department: row.department,
        total_booked_days: 0,
        total_logged_days: 0,
        total_version_active_days: 0,
        average_log_coverage_percent: 0,
        average_version_activity_percent: 0,
        repeated_under_logging_count: 0,
        repeated_low_visibility_count: 0,
        logged_without_booking_count: 0,
        projects_worked_on: 0,
        confidence_category: "high",
        primary_status: "aligned",
        status_counts: {},
      };
    }
    const artistSummary = artistSummaries[row.artist_id];
    artistSummary.total_booked_days += row.booked_days;
    artistSummary.total_logged_days += row.logged_days;
    artistSummary.total_version_active_days += row.version_active_days;
    artistSummary.status_counts[row.alignment_status] = (artistSummary.status_counts[row.alignment_status] || 0) + 1;
    if (row.alignment_status === "under_logged_active") artistSummary.repeated_under_logging_count += 1;
    if (row.alignment_status === "booked_low_visibility") artistSummary.repeated_low_visibility_count += 1;
    if (row.alignment_status === "logged_without_booking") artistSummary.logged_without_booking_count += 1;

    const departmentKey = row.department || "Unassigned";
    if (!departmentSummaries[departmentKey]) {
      departmentSummaries[departmentKey] = {
        department: departmentKey,
        booked_days: 0,
        logged_days: 0,
        version_active_days: 0,
        log_coverage_percent: 0,
        possible_hidden_labour_cost: 0,
        margin_risk: 0,
        low_visibility_weeks: 0,
        under_logging_weeks: 0,
        confidence_category: "high",
      };
    }
    const departmentSummary = departmentSummaries[departmentKey];
    departmentSummary.booked_days += row.booked_days;
    departmentSummary.logged_days += row.logged_days;
    departmentSummary.version_active_days += row.version_active_days;
    departmentSummary.possible_hidden_labour_cost += row.possible_hidden_labour_cost;
    departmentSummary.margin_risk += row.margin_at_risk_from_under_logging;
    if (row.alignment_status === "booked_low_visibility") departmentSummary.low_visibility_weeks += 1;
    if (row.alignment_status === "under_logged_active") departmentSummary.under_logging_weeks += 1;

    if (
      row.alignment_status !== "aligned" ||
      row.possible_hidden_labour_cost >= thresholds.highHiddenLabourCostDays * Math.max(row.artist_daily_rate, 1)
    ) {
      alerts.push({
        id: `alignment:${row.id}`,
        scope: "artist_project_week",
        scope_id: row.id,
        severity: row.possible_hidden_labour_cost >= thresholds.highHiddenLabourCostDays * Math.max(row.artist_daily_rate, 1)
          ? "critical"
          : reasonSeverity(row.reason_codes),
        title: `${row.project_name} • ${row.artist_name}`,
        summary: `${row.alignment_status.replaceAll("_", " ")} for ${row.week_start} with ${row.booked_days} booked days, ${row.logged_days} logged days, and ${row.version_active_days} version-active days.`,
        reason_codes: row.reason_codes,
      });
    }
  }

  for (const projectSummary of Object.values(projectSummaries)) {
    projectSummary.booked_days = roundNumber(projectSummary.booked_days);
    projectSummary.logged_days = roundNumber(projectSummary.logged_days);
    projectSummary.version_active_days = roundNumber(projectSummary.version_active_days);
    projectSummary.allocated_bid_days = roundNumber(projectSummary.allocated_bid_days);
    projectSummary.total_booked_labour_cost = roundNumber(projectSummary.total_booked_labour_cost);
    projectSummary.total_logged_labour_cost = roundNumber(projectSummary.total_logged_labour_cost);
    projectSummary.possible_hidden_labour_cost = roundNumber(projectSummary.possible_hidden_labour_cost);
    projectSummary.logged_coverage_percent = roundNumber((ratio(projectSummary.logged_minutes, projectSummary.booked_minutes) || 0) * 100, 1);
    projectSummary.version_activity_coverage_percent = roundNumber((ratio(projectSummary.version_active_days, projectSummary.booked_days) || 0) * 100, 1);
    projectSummary.confidence_category =
      projectSummary.number_of_booked_low_visibility_weeks > 0 || projectSummary.number_of_logged_without_booking_weeks > 0
        ? "medium"
        : projectSummary.number_of_under_logged_active_weeks > 0 || projectSummary.number_of_version_without_logs_weeks > 0
          ? "medium"
          : "high";
    projectSummary.source_alignment_summary = sourceAlignmentSummary(projectSummary.status_counts);
    projectSummary.primary_status = primaryStatusFromCounts(projectSummary.status_counts);
    projectSummary.margin_difference_due_to_alignment_gap = roundNumber(projectSummary.possible_hidden_labour_cost);
    if (typeof projectSummary.current_timelog_gross_margin === "number") {
      projectSummary.booking_cost_margin_estimate = roundNumber(
        projectSummary.current_timelog_gross_margin - projectSummary.possible_hidden_labour_cost,
      );
    }
  }

  for (const artistSummary of Object.values(artistSummaries)) {
    artistSummary.total_booked_days = roundNumber(artistSummary.total_booked_days);
    artistSummary.total_logged_days = roundNumber(artistSummary.total_logged_days);
    artistSummary.total_version_active_days = roundNumber(artistSummary.total_version_active_days);
    artistSummary.average_log_coverage_percent = roundNumber(
      (ratio(artistSummary.total_logged_days, artistSummary.total_booked_days) || 0) * 100,
      1,
    );
    artistSummary.average_version_activity_percent = roundNumber(
      (ratio(artistSummary.total_version_active_days, artistSummary.total_booked_days) || 0) * 100,
      1,
    );
    artistSummary.projects_worked_on = new Set(
      rows.filter((row) => row.artist_id === artistSummary.artist_id).map((row) => row.project_id),
    ).size;
    artistSummary.confidence_category =
      artistSummary.repeated_under_logging_count > 1 || artistSummary.repeated_low_visibility_count > 1
        ? "medium"
        : artistSummary.logged_without_booking_count > 1
          ? "medium"
          : "high";
    artistSummary.primary_status = primaryStatusFromCounts(artistSummary.status_counts);
  }

  for (const departmentSummary of Object.values(departmentSummaries)) {
    departmentSummary.booked_days = roundNumber(departmentSummary.booked_days);
    departmentSummary.logged_days = roundNumber(departmentSummary.logged_days);
    departmentSummary.version_active_days = roundNumber(departmentSummary.version_active_days);
    departmentSummary.log_coverage_percent = roundNumber(
      (ratio(departmentSummary.logged_days, departmentSummary.booked_days) || 0) * 100,
      1,
    );
    departmentSummary.possible_hidden_labour_cost = roundNumber(departmentSummary.possible_hidden_labour_cost);
    departmentSummary.margin_risk = roundNumber(departmentSummary.margin_risk);
    departmentSummary.confidence_category =
      departmentSummary.low_visibility_weeks > 1 || departmentSummary.under_logging_weeks > 1 ? "medium" : "high";
  }

  const summary: LabourAlignmentSummary = {
    booked_labour_cost: roundNumber(rows.reduce((sum, row) => sum + row.booked_labour_cost, 0)),
    logged_labour_cost: roundNumber(rows.reduce((sum, row) => sum + row.logged_labour_cost, 0)),
    possible_hidden_labour_cost: roundNumber(rows.reduce((sum, row) => sum + row.possible_hidden_labour_cost, 0)),
    log_coverage_percent: roundNumber(
      (ratio(
        rows.reduce((sum, row) => sum + row.logged_minutes, 0),
        rows.reduce((sum, row) => sum + row.booked_minutes, 0),
      ) || 0) * 100,
      1,
    ),
    version_activity_coverage_percent: roundNumber(
      (ratio(
        rows.reduce((sum, row) => sum + row.version_active_days, 0),
        rows.reduce((sum, row) => sum + row.booked_days, 0),
      ) || 0) * 100,
      1,
    ),
    low_confidence_weeks: rows.filter((row) => row.confidence_category === "low").length,
    margin_at_risk: roundNumber(rows.reduce((sum, row) => sum + row.margin_at_risk_from_under_logging, 0)),
    booked_days: roundNumber(rows.reduce((sum, row) => sum + row.booked_days, 0)),
    logged_days: roundNumber(rows.reduce((sum, row) => sum + row.logged_days, 0)),
    version_active_days: roundNumber(rows.reduce((sum, row) => sum + row.version_active_days, 0)),
    review_needed_weeks: rows.filter((row) => row.alignment_status !== "aligned").length,
  };

  return {
    rows,
    projectSummaries,
    artistSummaries,
    departmentSummaries,
    summary,
    alerts,
    thresholds,
    generatedAt,
  };
}
